// The click/share id (`cid`) — LIS-110 link tracking.
//
// One cid is minted at the moment of a click or share, rides the URL through
// every hop (share → edge redirect → web landing → app open), enriches every
// event in the sessions it starts (see setSessionAttribution), and lands raw
// in BigQuery. Attribution — source, content, viral chains, acquisition — is
// always a query over the raw events, never computed at capture.
//
// Pure string helpers, zero dependencies (no URL/URLSearchParams — must run
// identically on Hermes, workers, and browsers). Transport-free by lint rule.

export const CID_PARAM = "cid";

/**
 * Mints a new click/share id: millisecond timestamp (base36) plus ~51 bits of
 * randomness. Opaque, URL-safe, uniform across surfaces; collision odds are
 * negligible at click volumes, and a cid is an attribution key, not a
 * security token.
 */
export const mintCid = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** Appends `?cid=`/`&cid=` to a URL, keeping any #fragment at the end. */
export const appendCid = (url: string, cid: string): string => {
  const hashIndex = url.indexOf("#");
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${CID_PARAM}=${encodeURIComponent(cid)}${hash}`;
};

/** Reads the cid off a URL, path, or search string; null when absent. */
export const readCid = (url: string): string | null => {
  const match = /[?&]cid=([^&#]+)/.exec(url);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Removes the cid param (and its separator) from a URL or path, preserving
 * every other param and any #fragment — for address-bar/navigation cleanup
 * after ingest, so copied URLs stay clean and an organic copy simply mints no
 * cid.
 */
export const stripCid = (url: string): string =>
  url
    // cid with a following param: drop it, keep the separator for the next.
    .replace(/([?&])cid=[^&#]*&/, "$1")
    // cid as the last param (optionally before a #fragment): drop separator too.
    .replace(/[?&]cid=[^&#]*(#|$)/, "$1");
